/* eslint-disable */

/**
 * Email Reports Vuex Store Module
 * 
 * Manages state for the Email Reports feature including:
 * - Report configurations (daily/weekly/monthly)
 * - Available metrics
 * - Recipients
 * - Aggregate reports for multi-site customers
 */

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Available metric types organized by frequency
// NOTE: These type names MUST match the backend scheduler.js METRIC_TEMPLATES keys
const AVAILABLE_METRICS = {
  daily: [
    { type: 'todays_schedule', label: "Today's Schedule Summary", description: 'Appointments scheduled, expected production' },
    { type: 'overdue_tasks', label: 'Overdue Tasks Alert', description: 'Tasks past due date, assigned to whom' },
    { type: 'broken_appointments', label: 'Broken Appointments (Yesterday)', description: "Yesterday's cancellations and lost revenue" },
    { type: 'new_patients', label: 'New Patient Appointments', description: 'New patients scheduled for today' },
    { type: 'outstanding_claims', label: 'Outstanding Claims Alert', description: 'Claims aging > 30/60/90 days' }
  ],
  weekly: [
    { type: 'team_insights', label: 'Team Insights Summary', description: 'Follow-ups made, scheduled, conversion rates by employee' },
    { type: 'production_vs_collection', label: 'Production vs Collection', description: 'Net production, collection totals, % of goal' },
    { type: 'overdue_recall', label: 'Overdue Recall Pipeline', description: 'Overdue patients count & dollar value' },
    { type: 'unscheduled_treatment', label: 'Unscheduled Treatment Pipeline', description: 'Pending treatment value, patient count' },
    { type: 'task_completion', label: 'Task Completion Rate', description: '% completed by employee' },
    { type: 'broken_appointments', label: 'Broken Appointments Summary', description: 'Weekly cancellations, $ lost, rescheduled count' },
    { type: 'patient_balances', label: 'Patient Balances Aging', description: 'Outstanding AR by aging bucket' },
    { type: 'new_patients', label: 'New Patients This Week', description: 'Count and scheduled procedures' }
  ],
  monthly: [
    { type: 'monthly_production', label: 'Monthly Production Dashboard', description: 'Production vs goal by provider' },
    { type: 'collection_rate', label: 'Collection Rate Report', description: 'Insurance vs patient payments' },
    { type: 'provider_performance', label: 'Provider Performance', description: 'Production by provider, MTD vs goal' },
    { type: 'case_acceptance', label: 'Case Acceptance Report', description: 'Treatment acceptance %, $ value' },
    { type: 'hygiene_effectiveness', label: 'Hygiene Recall Effectiveness', description: 'Recall completion rate, hygiene production' },
    { type: 'claims_analysis', label: 'Insurance Claims Analysis', description: 'Claims submitted vs paid, denial rate' },
    { type: 'employee_performance', label: 'Employee Performance', description: 'Follow-ups & conversions by employee' }
  ],
};

// Common timezones for dental practices (US-focused)
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

// Default recipient roles: Admin (2) and Manager (3)
// These are the RoleIDs from the roles table that should receive reports by default
export const DEFAULT_RECIPIENT_ROLES = [2, 3];

const state = {
  // Report configurations by frequency
  configs: {
    daily: null,
    weekly: null,
    monthly: null
  },

  // Aggregate report configs (for multi-site customers)
  aggregateConfigs: {
    daily: null,
    weekly: null,
    monthly: null
  },
  
  // Available metrics (static reference)
  availableMetrics: AVAILABLE_METRICS,
  timezones: TIMEZONES,
  daysOfWeek: DAYS_OF_WEEK,
  
  // Report logs
  reportLogs: [],
  
  // UI state
  loading: false,
  saving: false,
  error: null,
  successMessage: null,
  
  // Preview
  previewHtml: null,
  previewLoading: false
};

const getters = {
  // Get config by frequency
  getConfigByFrequency: (state) => (frequency) => state.configs[frequency],
  getAggregateConfigByFrequency: (state) => (frequency) => state.aggregateConfigs[frequency],
  
  // Get available metrics - expose the full object
  availableMetrics: (state) => state.availableMetrics,
  
  // Get available metrics by frequency
  getMetricsByFrequency: (state) => (frequency) => state.availableMetrics[frequency] || [],
  
  // Check if any report is configured
  hasAnyReportConfigured: (state) => {
    return Object.values(state.configs).some(config => config && config.IsActive);
  },
  
  // Get all active configs
  activeConfigs: (state) => {
    return Object.entries(state.configs)
      .filter(([_, config]) => config && config.IsActive)
      .map(([frequency, config]) => ({ frequency, ...config }));
  },
  
  // Timezones and days
  timezones: (state) => state.timezones,
  daysOfWeek: (state) => state.daysOfWeek,
  
  // UI state
  isLoading: (state) => state.loading,
  isSaving: (state) => state.saving,
  error: (state) => state.error,
  successMessage: (state) => state.successMessage,
  
  // Preview
  previewHtml: (state) => state.previewHtml,
  previewLoading: (state) => state.previewLoading,
  
  // Report logs
  reportLogs: (state) => state.reportLogs
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_SAVING(state, saving) {
    state.saving = saving;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  SET_SUCCESS_MESSAGE(state, message) {
    state.successMessage = message;
  },
  
  CLEAR_MESSAGES(state) {
    state.error = null;
    state.successMessage = null;
  },
  
  SET_CONFIG(state, { frequency, config }) {
    state.configs[frequency] = config;
  },
  
  SET_ALL_CONFIGS(state, configs) {
    // Reset all configs first
    state.configs = {
      daily: null,
      weekly: null,
      monthly: null
    };
    
    // Set each config by frequency
    if (configs && Array.isArray(configs)) {
      configs.forEach(config => {
        if (config && config.Frequency) {
          state.configs[config.Frequency] = config;
        }
      });
    }
  },
  
  SET_AGGREGATE_CONFIG(state, { frequency, config }) {
    state.aggregateConfigs[frequency] = config;
  },
  
  SET_ALL_AGGREGATE_CONFIGS(state, configs) {
    state.aggregateConfigs = {
      daily: null,
      weekly: null,
      monthly: null
    };
    
    if (configs && Array.isArray(configs)) {
      configs.forEach(config => {
        if (config && config.Frequency) {
          state.aggregateConfigs[config.Frequency] = config;
        }
      });
    }
  },
  
  SET_REPORT_LOGS(state, logs) {
    state.reportLogs = logs;
  },
  
  SET_PREVIEW_HTML(state, html) {
    state.previewHtml = html;
  },
  
  SET_PREVIEW_LOADING(state, loading) {
    state.previewLoading = loading;
  }
};

const actions = {
  /**
   * Fetch all email report configurations for a site
   */
  async fetchConfigs({ commit, rootState }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      const customerID = rootState.customer.customerID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/config`, {
        params: { siteID }
      });
      
      commit('SET_ALL_CONFIGS', response.data.configs || []);
      
      // Also fetch aggregate configs if multi-site customer
      if (response.data.isMultiSite) {
        const aggregateResponse = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/aggregate`, {
          params: { customerID }
        });
        commit('SET_ALL_AGGREGATE_CONFIGS', aggregateResponse.data.configs || []);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching email report configs:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to load email report settings');
      return null;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Batch save all email report configurations (unified save)
   * Called from the redesigned EmailReportsTab with a single "Save Settings" button.
   */
  async saveAllConfigs({ commit, rootState }, payload) {
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);

    try {
      const siteID = rootState.customer.siteID;
      const customerID = rootState.customer.customerID;
      const user = rootState.customer.user;

      const requestPayload = {
        ...payload,
        SiteID: siteID,
        CustomerID: customerID,
        CreatedBy: user?.EmployeeID
      };

      const response = await axios.put(
        `${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/configs`,
        requestPayload
      );

      // Update configs in store from response
      if (response.data.configs) {
        for (const config of response.data.configs) {
          commit('SET_CONFIG', { frequency: config.Frequency, config });
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error saving email report configs:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to save report settings');
      throw error;
    } finally {
      commit('SET_SAVING', false);
    }
  },

  /**
   * Save/Update a report configuration (legacy per-frequency save)
   */
  async saveConfig({ commit, rootState, dispatch }, { frequency, configData }) {
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      const customerID = rootState.customer.customerID;
      const user = rootState.customer.user;
      
      const payload = {
        ...configData,
        SiteID: siteID,
        CustomerID: customerID,
        Frequency: frequency,
        CreatedBy: user?.EmployeeID
      };
      
      // If config has an ID, it's an update
      const method = configData.ConfigID ? 'put' : 'post';
      const url = configData.ConfigID 
        ? `${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/config/${configData.ConfigID}`
        : `${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/config`;
      
      const response = await axios[method](url, payload);
      
      commit('SET_CONFIG', { frequency, config: response.data.config });
      commit('SET_SUCCESS_MESSAGE', `${frequency.charAt(0).toUpperCase() + frequency.slice(1)} report settings saved successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        commit('SET_SUCCESS_MESSAGE', null);
      }, 3000);
      
      return response.data.config;
    } catch (error) {
      console.error('Error saving email report config:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to save report settings');
      throw error;
    } finally {
      commit('SET_SAVING', false);
    }
  },
  
  /**
   * Toggle a report config active/inactive
   */
  async toggleConfigActive({ commit, state, dispatch }, { frequency, isActive }) {
    const config = state.configs[frequency];
    if (!config) return;
    
    return dispatch('saveConfig', {
      frequency,
      configData: {
        ...config,
        IsActive: isActive
      }
    });
  },
  
  /**
   * Save aggregate report configuration
   */
  async saveAggregateConfig({ commit, rootState }, { frequency, configData }) {
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    
    try {
      const customerID = rootState.customer.customerID;
      const user = rootState.customer.user;
      
      const payload = {
        ...configData,
        CustomerID: customerID,
        Frequency: frequency,
        CreatedBy: user?.EmployeeID
      };
      
      const method = configData.AggregateConfigID ? 'put' : 'post';
      const url = configData.AggregateConfigID 
        ? `${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/aggregate/${configData.AggregateConfigID}`
        : `${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/aggregate`;
      
      const response = await axios[method](url, payload);
      
      commit('SET_AGGREGATE_CONFIG', { frequency, config: response.data.config });
      commit('SET_SUCCESS_MESSAGE', 'Aggregate report settings saved successfully');
      
      setTimeout(() => {
        commit('SET_SUCCESS_MESSAGE', null);
      }, 3000);
      
      return response.data.config;
    } catch (error) {
      console.error('Error saving aggregate config:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to save aggregate report settings');
      throw error;
    } finally {
      commit('SET_SAVING', false);
    }
  },
  
  /**
   * Send a test report
   */
  async sendTestReport({ commit, rootState, state }, { frequency, email }) {
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    
    try {
      const siteID = rootState.customer.siteID;
      const config = state.configs[frequency];
      
      // Use the email-scheduler endpoint that goes through the full pipeline
      const response = await axios.post(`${process.env.VUE_APP_TEAMIO_API_URL}/email-scheduler/test`, {
        siteID,
        configId: config?.ConfigID,
        frequency,
        testEmail: email
      });
      
      commit('SET_SUCCESS_MESSAGE', `Test ${frequency} report sent to ${email}`);
      
      setTimeout(() => {
        commit('SET_SUCCESS_MESSAGE', null);
      }, 5000);
      
      return response.data;
    } catch (error) {
      console.error('Error sending test report:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to send test report');
      throw error;
    } finally {
      commit('SET_SAVING', false);
    }
  },
  
  /**
   * Get report preview HTML
   */
  async getPreview({ commit, rootState }, frequency) {
    commit('SET_PREVIEW_LOADING', true);
    commit('SET_PREVIEW_HTML', null);
    
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/preview`, {
        params: { siteID, frequency }
      });
      
      commit('SET_PREVIEW_HTML', response.data.html);
      return response.data.html;
    } catch (error) {
      console.error('Error getting report preview:', error);
      commit('SET_ERROR', 'Failed to generate preview');
      return null;
    } finally {
      commit('SET_PREVIEW_LOADING', false);
    }
  },
  
  /**
   * Fetch report send logs
   */
  async fetchReportLogs({ commit, rootState }, { limit = 50 } = {}) {
    try {
      const siteID = rootState.customer.siteID;
      
      const response = await axios.get(`${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/logs`, {
        params: { siteID, limit }
      });
      
      commit('SET_REPORT_LOGS', response.data.logs || []);
      return response.data.logs;
    } catch (error) {
      console.error('Error fetching report logs:', error);
      return [];
    }
  },
  
  /**
   * Delete a report configuration
   */
  async deleteConfig({ commit, state }, { frequency }) {
    const config = state.configs[frequency];
    if (!config || !config.ConfigID) return;
    
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    
    try {
      await axios.delete(`${process.env.VUE_APP_TEAMIO_API_URL}/email-reports/config/${config.ConfigID}`);
      
      commit('SET_CONFIG', { frequency, config: null });
      commit('SET_SUCCESS_MESSAGE', `${frequency.charAt(0).toUpperCase() + frequency.slice(1)} report deleted`);
      
      setTimeout(() => {
        commit('SET_SUCCESS_MESSAGE', null);
      }, 3000);
    } catch (error) {
      console.error('Error deleting config:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to delete report');
      throw error;
    } finally {
      commit('SET_SAVING', false);
    }
  },
  
  /**
   * Clear all messages
   */
  clearMessages({ commit }) {
    commit('CLEAR_MESSAGES');
  },
  
  /**
   * Initialize default config structure for a frequency
   */
  initializeConfig({ commit, rootState }, frequency) {
    const siteID = rootState.customer.siteID;
    const customerID = rootState.customer.customerID;
    
    const defaultConfig = {
      ConfigID: null,
      SiteID: siteID,
      CustomerID: customerID,
      Frequency: frequency,
      DeliveryTime: '07:00',
      Timezone: 'America/New_York',
      DayOfWeek: 1,  // Monday
      DayOfMonth: 1,
      IsActive: false,
      RecipientType: 'by_role',
      RecipientRoles: [...DEFAULT_RECIPIENT_ROLES],
      Metrics: [],
      Recipients: []
    };
    
    commit('SET_CONFIG', { frequency, config: defaultConfig });
    return defaultConfig;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
